import { forwardRef } from "react";
import styled from "@emotion/styled";
import { createLink, CreateLinkProps } from "@tanstack/react-router";
import { Flex } from "styles/utility-components";

type Props = {
  width?: number;
  height?: number;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  fullWidth?: boolean;
  backgroundColor?: string;
  borderRadius?: number;
  href?: string;
  // TanStack Router destination — navigates within the SPA (no full reload).
  // Prefer this over `href` for internal links; keep `href` for external URLs.
  //
  // Intentionally typed as a loose `string` rather than the strict route union
  // (cf. RouterTextLink): every current Card link target is a dynamic value —
  // query-string URLs (framework filters, /contacts?…) or external/mailto links
  // (ContactCard) — none are static route literals, so strict typing would add
  // no call-site safety here while breaking the external-URL use. Validate
  // internal targets at the source via appRoutes (`satisfies LinkProps`).
  to?: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
  padding?: number;
  disabled?: boolean;
};

function Card({ children, onClick, href, to, params, search, disabled, ...rest }: Props) {
  const isLink = !!href || !!to;
  const interactive = (!!onClick || isLink) && !disabled;

  const onClickHandler = onClick
    ? (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (disabled) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onClick(e);
        if (!isLink) {
          e.preventDefault();
        }
        e.stopPropagation();
      }
    : undefined;

  if (to) {
    // Cast confined here: CardContainer's styling props don't unify with
    // createLink's CreateLinkProps, mirroring the Button router-link integration.
    return (
      <RouterCard
        {...({
          ...rest,
          to,
          params,
          search,
          disabled,
          onClick: onClickHandler,
          tabIndex: disabled ? -1 : 0,
          "aria-disabled": disabled,
          "data-interactive": interactive,
        } as unknown as CreateLinkProps)}
      >
        {children}
      </RouterCard>
    );
  }

  // A card with an onClick and no href is a div that takes focus but answers no key, so a
  // keyboard user could tab to it and not activate it. Give it the role it is already
  // behaving as, and the Enter/Space a button would have.
  const actsAsButton = !!onClick && !href;
  const onKeyDown = actsAsButton
    ? (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled || (e.key !== "Enter" && e.key !== " ")) {
          return;
        }
        e.preventDefault();
        e.currentTarget.click();
      }
    : undefined;

  return (
    <CardContainer
      as={href ? "a" : "div"}
      onClick={onClickHandler}
      onKeyDown={onKeyDown}
      role={actsAsButton ? "button" : undefined}
      tabIndex={disabled ? -1 : 0}
      href={disabled ? undefined : href}
      aria-disabled={disabled}
      data-interactive={interactive}
      {...rest}
    >
      {children}
    </CardContainer>
  );
}

export default Card;

type CardContainerProps = Pick<
  Props,
  "width" | "height" | "fullWidth" | "backgroundColor" | "padding" | "borderRadius"
> & { href?: string };

const CardContainer = styled(Flex)<CardContainerProps>(
  ({ theme, width, height, fullWidth, backgroundColor, padding, borderRadius }) => ({
    width: fullWidth ? "100%" : width,
    height,
    backgroundColor: backgroundColor ?? theme.colors.sysBackgroundDefault,
    flexDirection: "column",
    borderRadius: borderRadius ?? 4,
    border: `1px solid ${theme.colors.sysBorderDecorative}`,
    padding: padding ?? 16,
    gap: 8,
    transition: "background-color 0.2s, border-color 0.2s, opacity 0.2s",
    "&[data-interactive='true']": {
      cursor: "pointer",
      "&:hover:not(:has(.cardHoverDisable:hover))": {
        backgroundColor: theme.colors.sysPrimarySubtle,
        borderColor: theme.colors.sysPrimaryHover,
      },
      "&:focus": {
        borderColor: theme.colors.sysPrimaryPressed,
        backgroundColor: theme.colors.sysPrimarySubtleAlt,
      },
    },
    "&[aria-disabled='true']": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  }),
);

// Anchor variant for the router-link path. createLink resolves `to`/`search`
// into href + onClick before they reach CardContainer (rendered as an <a>).
const CardLinkInner = forwardRef<HTMLAnchorElement, Record<string, unknown>>((props, ref) => (
  <CardContainer as="a" ref={ref as never} {...props} />
));
CardLinkInner.displayName = "CardLinkInner";

const RouterCard = createLink(
  CardLinkInner as unknown as (props: CreateLinkProps) => React.ReactNode,
);
